import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  acceptStaffInvite,
  getStaffInvite,
} from "../api/staffInvites";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
} from "../ui/modalStyles";

const LOGIN_BTN_PRIMARY = `${BTN_PRIMARY} inline-flex items-center justify-center min-w-[148px] text-center leading-none`;
const LOGIN_BTN_OUTLINE = `${BTN_OUTLINE} inline-flex items-center justify-center min-w-[148px] text-center leading-none`;

function parseInviteError(err) {
  const status = Number(err?.response?.status || 0);
  const detail = err?.response?.data?.detail;
  const code =
    typeof detail === "string"
      ? detail
      : detail?.code || detail?.detail || "";
  const message =
    (typeof detail === "string" && detail) ||
    detail?.message ||
    err?.message ||
    "Unable to process invitation.";
  return { status, code: String(code || "").toUpperCase(), message };
}

function formatExpiry(expiresAt) {
  if (!expiresAt) return "Does not expire";
  try {
    return new Date(expiresAt).toLocaleString();
  } catch (_) {
    return String(expiresAt);
  }
}

export default function StaffInviteAccept() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading");
  const [invite, setInvite] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    username: "",
    password: "",
    confirm_password: "",
  });

  useEffect(() => {
    let isMounted = true;
    const loadInvite = async () => {
      setStatus("loading");
      setSubmitError("");
      try {
        const data = await getStaffInvite(token);
        if (!isMounted) return;
        setInvite(data);
        setStatus("valid");
      } catch (err) {
        if (!isMounted) return;
        const parsed = parseInviteError(err);
        if (parsed.status === 410 && parsed.code === "INVITE_EXPIRED") {
          setStatus("expired");
          return;
        }
        if (parsed.status === 409 && parsed.code === "INVITE_ALREADY_ACCEPTED") {
          setStatus("used");
          return;
        }
        setStatus("invalid");
      }
    };
    if (token) {
      loadInvite();
    } else {
      setStatus("invalid");
    }
    return () => {
      isMounted = false;
    };
  }, [token]);

  const expirationLabel = useMemo(
    () => formatExpiry(invite?.expires_at),
    [invite?.expires_at]
  );
  const usesExistingAccount = invite?.accept_mode === "existing_account" || invite?.account_exists;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    const firstName = form.first_name.trim();
    const lastName = form.last_name.trim();
    const username = form.username.trim();
    const password = form.password;
    const confirmPassword = form.confirm_password;

    if (!usesExistingAccount && (!firstName || !lastName)) {
      setSubmitError("First name and last name are required.");
      return;
    }
    if (!password) {
      setSubmitError("Password is required.");
      return;
    }
    if (!usesExistingAccount && password !== confirmPassword) {
      setSubmitError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = usesExistingAccount
        ? { password }
        : {
            first_name: firstName,
            last_name: lastName,
            username: username || null,
            password,
          };
      await acceptStaffInvite(token, payload);
      setStatus("success");
    } catch (err) {
      const parsed = parseInviteError(err);
      if (parsed.status === 410 && parsed.code === "INVITE_EXPIRED") {
        setStatus("expired");
        return;
      }
      if (parsed.status === 409 && parsed.code === "INVITE_ALREADY_ACCEPTED") {
        setStatus("used");
        return;
      }
      setSubmitError(parsed.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (status === "loading") {
      return <p className="text-slate-500">Loading invitation...</p>;
    }

    if (status === "expired") {
      return (
        <div className="space-y-4">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
            Invitation link expired
          </h2>
          <p className="text-slate-600">
            This invitation link has expired. Please contact your salon admin to resend a new link.
          </p>
          <div className="pt-4">
            <Link
              to="/login"
              className={LOGIN_BTN_PRIMARY}
            >
              Go to Login
            </Link>
          </div>
        </div>
      );
    }

    if (status === "used") {
      return (
        <div className="space-y-4">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
            Invitation already used
          </h2>
          <p className="text-slate-600">
            This invitation has already been accepted. Please contact your salon admin if you still need access.
          </p>
          <div className="pt-4">
            <Link
              to="/login"
              className={LOGIN_BTN_PRIMARY}
            >
              Go to Login
            </Link>
          </div>
        </div>
      );
    }

    if (status === "invalid") {
      return (
        <div className="space-y-4">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
            Invitation not found
          </h2>
          <p className="text-slate-600">
            This invitation link is invalid. Please contact your salon admin to request a new invite.
          </p>
          <div className="pt-4">
            <Link
              to="/login"
              className={LOGIN_BTN_PRIMARY}
            >
              Go to Login
            </Link>
          </div>
        </div>
      );
    }

    if (status === "success") {
      return (
        <div className="space-y-4">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
            {usesExistingAccount ? "Salon access connected" : "Account activated"}
          </h2>
          <p className="text-slate-600">
            {usesExistingAccount
              ? "You can now log in with your existing Ellyanna account."
              : "Account activated. You can now log in."}
          </p>
          <div className="pt-4">
            <Link
              to="/login"
              className={LOGIN_BTN_PRIMARY}
            >
              Go to Login
            </Link>
          </div>
        </div>
      );
    }

    if (invite?.already_has_access) {
      return (
        <div className="space-y-4">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
            Already connected
          </h2>
          <p className="text-slate-600">
            This email already has access to {invite?.salon_name || "this salon"}.
            Log in with the existing Ellyanna account.
          </p>
          <div className="pt-4">
            <Link to="/login" className={LOGIN_BTN_PRIMARY}>
              Go to Login
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
            You&apos;re invited to join {invite?.salon_name || "this salon"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {usesExistingAccount
              ? "This email already has an Ellyanna account. Use the current password to join this salon."
              : "Complete your account setup to start as a service provider."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className={LABEL_TEXT}>Email</div>
            <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
              {invite?.email}
            </div>
          </div>
          <div>
            <div className={LABEL_TEXT}>Invitation Expiration</div>
            <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
              {expirationLabel}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!usesExistingAccount ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_TEXT}>First Name</label>
                  <input
                    type="text"
                    required
                    value={form.first_name}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, first_name: event.target.value }))
                    }
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                  />
                </div>
                <div>
                  <label className={LABEL_TEXT}>Last Name</label>
                  <input
                    type="text"
                    required
                    value={form.last_name}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, last_name: event.target.value }))
                    }
                    className={`mt-1 w-full ${FIELD_CLASS}`}
                  />
                </div>
              </div>

              <div>
                <label className={LABEL_TEXT}>Username (optional)</label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, username: event.target.value }))
                  }
                  className={`mt-1 w-full ${FIELD_CLASS}`}
                />
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
              This person can use the same Ellyanna login at more than one salon.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_TEXT}>
                {usesExistingAccount ? "Current Ellyanna Password" : "Password"}
              </label>
              <input
                type="password"
                required
                value={form.password}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, password: event.target.value }))
                }
                className={`mt-1 w-full ${FIELD_CLASS}`}
              />
            </div>
            {!usesExistingAccount ? (
              <div>
                <label className={LABEL_TEXT}>Confirm Password</label>
                <input
                  type="password"
                  required
                  value={form.confirm_password}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      confirm_password: event.target.value,
                    }))
                  }
                  className={`mt-1 w-full ${FIELD_CLASS}`}
                />
              </div>
            ) : null}
          </div>

          {usesExistingAccount ? (
            <div className="text-sm text-slate-500">
              Forgot the password?{" "}
              <Link to="/forgot-password" className="font-semibold text-slate-800 underline">
                Reset it first
              </Link>
              , then come back to this invite link.
            </div>
          ) : null}

          {submitError ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {submitError}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Link to="/login" className={LOGIN_BTN_OUTLINE}>
              Go to Login
            </Link>
            <button
              type="submit"
              className={`${BTN_PRIMARY} inline-flex items-center justify-center leading-none`}
              disabled={submitting}
            >
              {submitting
                ? usesExistingAccount ? "Connecting..." : "Activating..."
                : usesExistingAccount ? "Join Salon" : "Activate Account"}
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div className="px-4 py-6">
      <div className="mx-auto w-full max-w-[900px]">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Staff Invitation
            </h1>
          </div>
          <div className="px-6 py-6">{renderBody()}</div>
        </div>
      </div>
    </div>
  );
}
